import React, { useState, useEffect } from "react";
import "./MusicManageListing.css";
import { useNavigate } from "react-router-dom";
import ServicePath from "../../../services/webservice";
import Swal from "sweetalert2";

const MusicManagelisting = () => {
    const navigate = useNavigate();
    
    // States for data and pagination
    const [musicList, setMusicList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    
    // Filter and Sort states
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [sortField, setSortField] = useState("createdDate");
    const [sortOrder, setSortOrder] = useState("desc");

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Reset to page 1 if any filter changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, sortField, sortOrder]);

    // Fetch data when filters or page changes
    useEffect(() => {
        fetchMusicList();
    }, [debouncedSearch, page, sortField, sortOrder]);

    const fetchMusicList = async () => {
        setLoading(true);
        try {
            const payload = {
                search: debouncedSearch,
                page,
                pageSize,
                sortField,
                sortOrder
            };
            const response = await fetch(`${ServicePath}api/getMusicList`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.response === 'S') {
                setMusicList(data.data);
                setTotalCount(data.totalCount || 0);
            }
        } catch (error) {
            console.error("Error fetching music list", error);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setSearch("");
        setPage(1);
        setSortField("createdDate");
        setSortOrder("desc");
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${ServicePath}api/deleteMusic`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ id })
                    });
                    const data = await response.json();
                    if (data.response === 'S') {
                        Swal.fire(
                            'Deleted!',
                            'Music track has been deleted.',
                            'success'
                        );
                        fetchMusicList();
                    } else {    
                        Swal.fire('Error', data.message, 'error');
                    }
                } catch (error) {
                    console.error("Error deleting music track", error);
                    Swal.fire('Error', 'An error occurred while deleting', 'error');
                }
            }
        });
    };

    // Calculate pagination details
    const totalPages = Math.ceil(totalCount / pageSize);
    const startIndex = (page - 1) * pageSize + 1;
    const endIndex = Math.min(page * pageSize, totalCount);

    return (
        <div className="music-list-container">

            {/* Header */}
            <div className="music-list-header">
                <h2 className="music-list-title">Music Management</h2>
                <button className="add-music-btn" onClick={() => navigate("/add-music")}>
                    + Add Music
                </button>
            </div>

            {/* Filter Section */}
            <div className="filter-section">
                <div className="search-box">
                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg" className="music-search-icon"><path d="M10 18a7.952 7.952 0 0 0 4.897-1.688l4.396 4.396 1.414-1.414-4.396-4.396A7.952 7.952 0 0 0 18 10c0-4.411-3.589-8-8-8s-8 3.589-8 8 3.589 8 8 8zm0-14c3.309 0 6 2.691 6 6s-2.691 6-6 6-6-2.691-6-6 2.691-6 6-6z"></path></svg>
                    <input
                        type="text"
                        placeholder="Search by Title or Artist"
                        className="search-inputs"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button className="reset-btn" onClick={handleReset} style={{ width: '100%', border:"1px solid #d6d6d6ff" }}>Reset</button>
                </div>
            </div>

            {/* Table */}
            <div className="table-wrapper">
                <table className="music-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Icon</th>
                            <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                                Title {sortField === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('artist')} style={{ cursor: 'pointer' }}>
                                Artist {sortField === 'artist' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                                Category {sortField === 'category' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('duration')} style={{ cursor: 'pointer' }}>
                                Duration {sortField === 'duration' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>Loading...</td>
                            </tr>
                        ) : musicList.length > 0 ? (
                            musicList.map((music, index) => (
                                <tr key={music._id}>
                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                    <td>
                                        {music.icon ? (
                                            <i className="material-icons" style={{ fontSize: '20px', color: music.colorHex || '#6366f1', verticalAlign: 'middle' }}>
                                                {music.icon}
                                            </i>
                                        ) : (
                                            <span style={{ color: '#ccc' }}>-</span>
                                        )}
                                    </td>
                                    <td>{music.title}</td>
                                    <td>{music.artist}</td>
                                    <td>{music.category}</td>
                                    <td>{music.duration}</td>
                                    <td>
                                        <span className={music.status === 'A' || music.status === true ? 'status-active' : 'status-inactive'}>
                                            {music.status === 'A' || music.status === true ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <button className="edit-btn" onClick={() => navigate(`/edit-music/${music.Refid}`)}>Edit</button>
                                        <button className="delete-btn" onClick={() => handleDelete(music.Refid)}>Delete</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>No music tracks found</td>
                            </tr>
                        )}
                    </tbody>

                    <tfoot>
                        <tr>
                            <td colSpan="8">
                                <div className="pagination-music">
                                    <div className="pagination-info">
                                        Showing {totalCount > 0 ? startIndex : 0} - {endIndex} of {totalCount} Music
                                    </div>
                                    <div className="pagination-buttons">
                                        <button 
                                            className="page-btn" 
                                            onClick={() => setPage(page - 1)}
                                            disabled={page === 1}
                                        >
                                            ◀
                                        </button>
                                        
                                        {[...Array(totalPages)].map((_, i) => (
                                            <button 
                                                key={i + 1}
                                                className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                                                onClick={() => setPage(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}

                                        <button 
                                            className="page-btn" 
                                            onClick={() => setPage(page + 1)}
                                            disabled={page === totalPages || totalPages === 0}
                                        >
                                            ▶
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default MusicManagelisting;